import { loadStripe } from '@stripe/stripe-js';
import { authService } from './authService';

// Hardcoded keys for testing - use these as fallbacks
const STRIPE_TEST_KEY = 'pk_test_51RKbN6QLfmPA8QFsgDKalBu1NMKkLfdUfwOnTyJCxjeTHahCXlgymcFatnErzQ3aBo3cjZcy6OfVtwS2Ks5QxPD300KMv2N4LZ';
const STRIPE_LIVE_KEY = 'pk_live_51OqXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX';

// Cache for Stripe instances
const stripeInstances = {};

// Determine if we're in development or production
const isDevelopment = process.env.REACT_APP_API_URL?.includes('.dev') || process.env.NODE_ENV === 'development';

/**
 * Get a Stripe instance with the appropriate key
 * @param {string} keyType 'TEST' or 'LIVE'
 * @param {string} customKey Optional custom key to use instead of the hardcoded keys
 * @returns {Promise<Stripe>} A Stripe instance
 */
export const getStripeInstance = async (keyType = 'LIVE', customKey = null) => {
  // Force TEST key in development, unless a custom key is provided
  const effectiveKeyType = customKey ? keyType : (isDevelopment ? 'TEST' : keyType);
  
  // Determine which key to use
  const key = customKey || (effectiveKeyType === 'TEST' ? STRIPE_TEST_KEY : STRIPE_LIVE_KEY);
  
  // Check if we already have an instance for this key
  if (stripeInstances[key]) {
    return stripeInstances[key];
  }
  
  // Create a new instance
  try {
    console.log(`Initializing Stripe with ${effectiveKeyType} key in ${isDevelopment ? 'development' : 'production'} environment`);
    const stripe = await loadStripe(key);
    
    if (!stripe) {
      throw new Error(`Failed to initialize Stripe with ${effectiveKeyType} key`);
    }
    
    // Cache the instance
    stripeInstances[key] = stripe;
    return stripe;
  } catch (error) {
    console.error(`Error initializing Stripe with ${effectiveKeyType} key:`, error);
    throw error;
  }
};

/**
 * Create a checkout session and redirect to Stripe
 * @param {Object} sessionData The checkout session data from the API
 * @returns {Promise<void>}
 */
export const redirectToCheckout = async (sessionData) => {
  if (!sessionData || !sessionData.success || !sessionData.sessionId) {
    throw new Error('Invalid session data');
  }
  
  try {
    // Get the appropriate Stripe instance based on the session data
    const keyType = sessionData.key_type || 'LIVE';
    console.log(`Using ${keyType} key for session ${sessionData.sessionId}`);
    
    // Use the publishable key from the server if available
    const customKey = sessionData.publishable_key || null;
    const stripe = await getStripeInstance(keyType, customKey);
    
    // Redirect to checkout
    const { error } = await stripe.redirectToCheckout({
      sessionId: sessionData.sessionId
    });
    
    if (error) {
      throw new Error(`Error redirecting to checkout: ${error.message}`);
    }
  } catch (error) {
    console.error('Error in redirectToCheckout:', error);
    throw error;
  }
};

/**
 * Force a checkout session to use a specific key type
 * This is useful for testing but may fail if the session type doesn't match the key type
 * @param {Object} sessionData The checkout session data from the API
 * @param {string} forceKeyType 'TEST' or 'LIVE'
 * @returns {Promise<void>}
 */
export const forceCheckoutWithKeyType = async (sessionData, forceKeyType) => {
  if (!sessionData || !sessionData.success || !sessionData.sessionId) {
    throw new Error('Invalid session data');
  }
  
  // Check if we're trying to use a test key with a live session or vice versa
  const sessionKeyType = sessionData.key_type || 'LIVE';
  if (sessionKeyType !== forceKeyType) {
    console.warn(`Warning: Trying to use a ${forceKeyType} key with a ${sessionKeyType} session. This will likely fail.`);
  }
  
  try {
    const stripe = await getStripeInstance(forceKeyType);
    
    // Redirect to checkout
    const { error } = await stripe.redirectToCheckout({
      sessionId: sessionData.sessionId
    });
    
    if (error) {
      throw new Error(`Error redirecting to checkout with ${forceKeyType} key: ${error.message}`);
    }
  } catch (error) {
    console.error(`Error in forceCheckoutWithKeyType (${forceKeyType}):`, error);
    throw error;
  }
};

/**
 * Create a new checkout session with the correct key type
 * This is the most reliable way to handle checkout
 * @param {Object} options The options for creating the checkout session
 * @returns {Promise<void>}
 */
export const createAndOpenCheckout = async (options) => {
  try {
    const {
      playerId,
      parentId,
      teamId,
      matchId,
      amount,
      paymentType,
      successUrl,
      cancelUrl,
      isSubscription,
      subscriberType
    } = options;

    // Validate required fields based on payment type
    if (!teamId) {
      throw new Error('Missing required field: teamId is required');
    }

    // For Pro payments, we need either playerId or parentId
    if (paymentType?.startsWith('pro_')) {
      if (!playerId && !parentId) {
        throw new Error('Missing required field: playerId or parentId is required for Pro payments');
      }
    } else {
      // For match payments
      if (!playerId) {
        throw new Error('Missing required field: playerId is required for match payments');
      }
      if (!matchId) {
        throw new Error('Missing required field: matchId is required for match payments');
      }
    }

    // Get JWT token using authService
    const token = authService.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated. Please log in again.');
    }

    // Add environment flag to the request
    const response = await fetch(`${process.env.REACT_APP_API_URL}/create-checkout-session.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        matchId: matchId || null,
        playerId: playerId || null,
        parentId: parentId || null,
        teamId: teamId,
        amount: amount,
        paymentType: paymentType,
        successUrl: successUrl,
        cancelUrl: cancelUrl,
        isSubscription: isSubscription,
        subscriberType: subscriberType || 'player',
        environment: isDevelopment ? 'development' : 'production'
      })
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Server response:', data);
      throw new Error(data.error || data.message || 'Failed to create checkout session');
    }

    // Check if we have a session ID in the response
    if (data.sessionId) {
      // Use our existing redirectToCheckout helper
      await redirectToCheckout({
        success: true,
        sessionId: data.sessionId,
        key_type: isDevelopment ? 'TEST' : 'LIVE',
        publishable_key: data.publishable_key
      });
    } else if (data.sessionUrl) {
      // Fallback to direct URL if provided
      window.location.href = data.sessionUrl;
    } else {
      throw new Error('No session information returned from server');
    }
  } catch (error) {
    console.error('Error in createAndOpenCheckout:', error);
    throw error;
  }
};

/**
 * Test if the Stripe public key is valid
 * @param {string} keyType 'TEST' or 'LIVE'
 * @returns {Promise<boolean>} Whether the key is valid
 */
export const testStripeKey = async (keyType = 'LIVE') => {
  try {
    const stripe = await getStripeInstance(keyType);
    return !!stripe;
  } catch (error) {
    console.error(`Error testing Stripe ${keyType} key:`, error);
    return false;
  }
};

export default {
  getStripeInstance,
  redirectToCheckout,
  forceCheckoutWithKeyType,
  createAndOpenCheckout,
  testStripeKey,
  STRIPE_TEST_KEY,
  STRIPE_LIVE_KEY
}; 